// src/utils/pageBE.ts — SCE Issues
// La page « Pour le BE » (24/10) : provisoire, en attendant le cahier des
// charges du BE (maquette d'accueil BE du 20/10 pas encore validée). Deux
// listes, rien de plus :
//  - les problèmes ENVOYÉS au BE du métier (« Envoyer au BE », lus dans
//    SCE_Envois : la seule source qui dit à QUI, par adresse — l'activité ne
//    garde que les noms) ; « à vous » quand on en était destinataire ;
//  - les problèmes EN COURS du métier.
// Pur, testable hors ligne.

import { estActif, type Issue, type Metier } from '@/types/issue.types'

/** Une ligne de SCE_Envois qui concerne un problème (les notifications FNC-T y passent aussi). */
export interface LigneEnvoi {
  issueId: string
  destinataires: string[]
  /** Adresse de qui a envoyé. */
  expediteur: string
  note: string
  /** Date de l'envoi (ISO) : création de la ligne. */
  le: string
}

const ID_PROBLEME = /^P\d{4}-\d+$/i

/** Une ligne lue → l'envoi d'un problème ; null pour une notification FNC-T ou une ligne illisible. */
export function ligneEnvoi(fields: Record<string, unknown>, creeLe: string): LigneEnvoi | null {
  const id = String(fields.Title ?? '').trim()
  if (!ID_PROBLEME.test(id)) return null
  const texte = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')
  return {
    issueId: id.toUpperCase(),
    destinataires: texte(fields.Destinataires)
      .split(';')
      .map((d) => d.trim().toLowerCase())
      .filter((d) => d.includes('@')),
    expediteur: texte(fields.Expediteur).toLowerCase(),
    note: texte(fields.Note),
    le: creeLe,
  }
}

export interface EnvoiRecu {
  issue: Issue
  /** Le plus récent d'abord. */
  envois: LigneEnvoi[]
  /** Le dernier envoi (ISO). */
  dernier: string
  /** Au moins un envoi m'était adressé. */
  aVous: boolean
}

/**
 * Les problèmes envoyés à l'une des personnes du BE (ou à moi), un par
 * problème, le dernier envoyé en tête. Annulés et problèmes introuvables écartés.
 */
export function envoisAuBE(envois: readonly LigneEnvoi[], issues: readonly Issue[], be: readonly string[], moi: string): EnvoiRecu[] {
  const cles = new Set([...be, moi].map((u) => u.trim().toLowerCase()).filter(Boolean))
  const moiCle = moi.trim().toLowerCase()
  const parId = new Map(issues.map((i) => [i.id.toUpperCase(), i]))
  const groupes = new Map<string, EnvoiRecu>()
  for (const e of envois) {
    if (!e.destinataires.some((d) => cles.has(d))) continue
    const issue = parId.get(e.issueId)
    if (!issue || issue.statut === 'Annulé') continue
    const g = groupes.get(e.issueId) ?? { issue, envois: [], dernier: '', aVous: false }
    g.envois.push(e)
    if (e.le > g.dernier) g.dernier = e.le
    if (moiCle && e.destinataires.includes(moiCle)) g.aVous = true
    groupes.set(e.issueId, g)
  }
  const res = [...groupes.values()]
  for (const g of res) g.envois.sort((a, b) => b.le.localeCompare(a.le))
  return res.sort((a, b) => b.dernier.localeCompare(a.dernier))
}

/** Les problèmes en cours (à qualifier ou en cours) de ces métiers, le plus récent d'abord. */
export function problemesEnCours(issues: readonly Issue[], metiers: readonly Metier[]): Issue[] {
  return issues.filter((i) => estActif(i) && metiers.includes(i.metier)).sort((a, b) => b.dateDeclaration.localeCompare(a.dateDeclaration))
}

/** `2026-10-22T09:14:00Z` → `22/10` (heure de Paris). */
export function jourMoisParis(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Paris' }).format(d)
}
